import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Send, Zap, Clock } from 'react-feather';
import { DillButton } from '../Dill/DillButton';
import { DillModal } from '../Dill/DillModal';

interface AppSendAllStatementsModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (statementId?: string) => void;
  loading?: boolean;
  savedStatementMonths?: string[];
}

const AppSendAllStatementsModal: React.FC<AppSendAllStatementsModalProps> = ({
  open,
  handleClose,
  onSubmit,
  loading = false,
  savedStatementMonths,
}) => {
  const prevOpenRef = useRef(false);
  const [useSnapshot, setUseSnapshot] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const hasMonths = savedStatementMonths && savedStatementMonths.length > 0;

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open && !wasOpen) {
      setUseSnapshot(false);
      setSelectedMonth(hasMonths ? savedStatementMonths[0] : null);
    }
  }, [open, hasMonths, savedStatementMonths]);

  const handleSubmit = () => {
    onSubmit(useSnapshot && selectedMonth ? selectedMonth : undefined);
  };

  const isSendDisabled = loading || (useSnapshot && !selectedMonth);

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Send All Statements"
      maxWidth="min(90vw, 480px)"
      footer={
        <>
          <DillButton
            label="Cancel"
            variant="secondary"
            onClick={handleClose}
            isDisabled={loading}
          />
          <DillButton
            label={loading ? 'Sending…' : 'Send Statements'}
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={isSendDisabled}
          />
        </>
      }>
      <div className="space-y-5">
        {/* Mode selector */}
        <div className="grid grid-cols-2 gap-3">
          {/* Live */}
          <button
            type="button"
            onClick={() => setUseSnapshot(false)}
            className="relative flex flex-col items-start gap-2 p-4 rounded-xl border-2 text-left transition-all"
            style={{
              borderColor: !useSnapshot ? '#3b82f6' : '#e5e7eb',
              background: !useSnapshot ? '#eff6ff' : '#fafafa',
            }}>
            <div
              className="flex items-center justify-center w-8 h-8 rounded-lg"
              style={{ background: !useSnapshot ? '#dbeafe' : '#f1f5f9' }}>
              <Zap size={15} style={{ color: !useSnapshot ? '#2563eb' : '#94a3b8' }} />
            </div>
            <div>
              <p
                className="text-sm font-semibold"
                style={{ color: !useSnapshot ? '#1e40af' : '#334155' }}>
                Live
              </p>
              <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                Current balances right now
              </p>
            </div>
            {!useSnapshot && (
              <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-blue-500" />
            )}
          </button>

          {/* Snapshot */}
          <button
            type="button"
            onClick={() => setUseSnapshot(true)}
            className="relative flex flex-col items-start gap-2 p-4 rounded-xl border-2 text-left transition-all"
            style={{
              borderColor: useSnapshot ? '#3b82f6' : '#e5e7eb',
              background: useSnapshot ? '#eff6ff' : '#fafafa',
            }}>
            <div
              className="flex items-center justify-center w-8 h-8 rounded-lg"
              style={{ background: useSnapshot ? '#dbeafe' : '#f1f5f9' }}>
              <Clock size={15} style={{ color: useSnapshot ? '#2563eb' : '#94a3b8' }} />
            </div>
            <div>
              <p
                className="text-sm font-semibold"
                style={{ color: useSnapshot ? '#1e40af' : '#334155' }}>
                Snapshot
              </p>
              <p className="text-xs text-gray-500 mt-0.5 leading-snug">Saved end-of-month data</p>
            </div>
            {useSnapshot && (
              <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-blue-500" />
            )}
          </button>
        </div>

        {/* Month list — only shown when snapshot is selected */}
        {useSnapshot && (
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
              Select month
            </p>

            {hasMonths ? (
              <div className="flex flex-col gap-1.5 max-h-52 overflow-y-auto pr-0.5">
                {savedStatementMonths.map((month) => {
                  const isSelected = selectedMonth === month;
                  const isSending = loading && isSelected;
                  return (
                    <button
                      key={month}
                      type="button"
                      onClick={() => setSelectedMonth(month)}
                      disabled={loading}
                      className="w-full text-left px-4 py-3 rounded-xl border transition-all text-sm disabled:opacity-50 flex items-center justify-between group"
                      style={{
                        borderColor: isSelected ? '#3b82f6' : '#e5e7eb',
                        background: isSelected ? '#eff6ff' : 'white',
                        color: isSelected ? '#1e40af' : '#374151',
                        fontWeight: isSelected ? 600 : 400,
                      }}>
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: isSelected ? '#3b82f6' : '#e5e7eb' }}
                        />
                        <span>{moment(month, 'YYYY-MM').format('MMMM YYYY')}</span>
                      </div>
                      {isSending ? (
                        <span className="flex items-center gap-1 text-xs text-blue-500 font-medium">
                          <Send size={10} className="animate-pulse" />
                          Sending…
                        </span>
                      ) : (
                        isSelected && (
                          <span className="text-xs text-blue-400 font-medium">Selected</span>
                        )
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 py-6 rounded-xl bg-gray-50 border border-dashed border-gray-200">
                <Clock size={20} className="text-gray-300" />
                <p className="text-sm text-gray-400 text-center">
                  No snapshots saved yet.
                  <br />
                  <span className="font-medium text-gray-500">Save Snapshots</span> first to use
                  this option.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </DillModal>
  );
};

export default AppSendAllStatementsModal;
